import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Warning, Phone, WhatsappLogo } from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const VEHICLE_NAMES = { classe_e: "Mercedes Classe E", classe_v: "Mercedes Classe V", classe_s: "Mercedes Classe S" };

const ConnectPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");
  const [status, setStatus] = useState("verifying");
  const [booking, setBooking] = useState(null);

  const verify = useCallback(async () => {
    if (!bookingId) { setStatus("error"); return; }
    let attempts = 0;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/api/connect/payment/status/${bookingId}`);
        if (data.payment_status === "paid") {
          setBooking(data);
          setStatus("confirmed");
        } else {
          attempts++;
          if (attempts < 10) setTimeout(check, 2000);
          else setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };
    check();
  }, [bookingId]);

  useEffect(() => { verify(); }, [verify]);

  if (status === "verifying") {
    return (
      <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#1A1A1A]/20 border-t-[#1A1A1A] rounded-full animate-spin mx-auto mb-6" />
          <p className="font-heading text-xl text-[#1A1A1A]">Vérification du paiement...</p>
          <p className="text-[#1A1A1A]/50 text-sm mt-2">Veuillez patienter quelques instants</p>
        </div>
      </main>
    );
  }

  if (status === "confirmed" && booking) {
    return (
      <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="connect-payment-success">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full">
          <div className="text-center mb-8">
            <span className="font-heading text-xl tracking-[0.35em] text-[#1A1A1A] uppercase">Altura</span>
          </div>

          <div className="bg-white border border-[#1A1A1A]/10 p-8">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check size={28} weight="bold" className="text-green-600" />
            </div>
            <h1 className="font-heading text-2xl text-[#1A1A1A] text-center mb-2">Paiement confirmé</h1>
            <p className="text-[#1A1A1A]/60 text-sm text-center font-light mb-6">
              Votre service de transport premium a bien été confirmé. Notre équipe prépare actuellement votre trajet et vous communiquera les détails du chauffeur dans les meilleurs délais.
            </p>

            <div className="border-t border-[#1A1A1A]/10 pt-5 space-y-3">
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/50 text-sm">Date</span>
                <span className="text-[#1A1A1A] text-sm font-medium">{booking.pickup_date} à {booking.pickup_time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/50 text-sm">Véhicule</span>
                <span className="text-[#1A1A1A] text-sm">{VEHICLE_NAMES[booking.vehicle_type] || booking.vehicle_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/50 text-sm">Départ</span>
                <span className="text-[#1A1A1A] text-sm text-right max-w-[60%]">{booking.pickup_address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/50 text-sm">Destination</span>
                <span className="text-[#1A1A1A] text-sm text-right max-w-[60%]">{booking.dropoff_address}</span>
              </div>
              <div className="border-t border-[#1A1A1A]/10 pt-3 flex justify-between">
                <span className="text-[#1A1A1A] font-medium">Total</span>
                <span className="font-heading text-xl text-[#1A1A1A]">{booking.total_amount}€</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-6 space-y-3">
            <p className="text-[#1A1A1A]/50 text-xs">Un email de confirmation avec votre bon de commande PDF vous a été envoyé.</p>
            <p className="text-[#1A1A1A]/50 text-xs">Pour toute question :</p>
            <div className="flex justify-center gap-4">
              <a href="https://wa.me/33681969342" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#25D366] text-sm hover:underline">
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
              <a href="tel:+33681969342" className="inline-flex items-center gap-2 text-[#1A1A1A]/60 text-sm hover:underline">
                <Phone size={16} /> +33 6 81 96 93 42
              </a>
            </div>
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <Warning size={48} weight="light" className="text-[#1A1A1A]/40 mx-auto mb-4" />
        <h1 className="font-heading text-xl text-[#1A1A1A] mb-2">
          {status === "timeout" ? "Vérification en cours" : "Erreur"}
        </h1>
        <p className="text-[#1A1A1A]/60 text-sm font-light mb-6">
          {status === "timeout"
            ? "Le paiement est en cours de traitement. Vous recevrez un email de confirmation sous peu."
            : "Impossible de vérifier votre paiement. Contactez-nous pour assistance."}
        </p>
        <a href="tel:+33681969342" className="btn-dark inline-flex items-center gap-2">
          <Phone size={16} /> Nous contacter
        </a>
      </div>
    </main>
  );
};

export default ConnectPaymentSuccess;
